import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Unified Input component with size variants.
 *
 * Sizes:
 * - default: Standard height, suitable for most forms
 * - lg: Larger padding and text, rounded-xl, for hero sections and prominent forms
 */
const inputVariants = cva(
  "flex w-full border bg-background ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300",
  {
    variants: {
      inputSize: {
        // h-11 (44px) on mobile for touch accessibility, h-10 (40px) on desktop
        default: "h-11 md:h-10 rounded-md border-input px-3 py-2 text-base md:text-sm",
        lg: "py-3 md:py-4 px-4 md:px-6 text-base md:text-lg border-2 border-gray-200 focus:border-green-400 rounded-xl focus:ring-4 focus:ring-green-200",
        xl: "py-4 md:py-5 px-5 md:px-8 text-lg md:text-xl border-2 border-gray-200 focus:border-green-400 rounded-2xl focus:ring-4 focus:ring-green-200",
      },
    },
    defaultVariants: {
      inputSize: "default",
    },
  }
)

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {
  /** Input size variant. Use 'lg' for prominent forms and hero sections. */
  inputSize?: "default" | "lg" | "xl"
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, inputSize, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(inputVariants({ inputSize, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input, inputVariants }
